/**
 * Synthetic default basemap injection (ADR-023).
 *
 * When a gTwin scene has no entries in the `basemaps` slot (common for
 * engineering-only scenes that still need geospatial context), inject OSM
 * and Esri satellite raster Layers into the catalog so they:
 *   1. Render behind any other raster / vector / model layers via Giro3D's
 *      Map entity (the engine creates the Map lazily on the first map-layer
 *      add and stacks ColorLayers in add order).
 *   2. Appear as normal layer cards in `SceneTreePanel` so the user can
 *      toggle them off to verify high-resolution orthos are loading underneath.
 *
 * `style.maxZoom: 18` is honoured by the Giro3D raster path in
 * `giro3dRuntime.createGiro3DEntity` and is the safeguard against the
 * z=19+ retry-storm bug OSM exhibits.
 */
import type { Layer } from '@geosoft/viewer-sdk';
import type { SceneSlot } from '@/sdk-stub/gtwin-sdk';
import { isBasemapCrsCompatible } from './basemapSlotManager';
import { kindCanSwitchRenderFrame } from '@/engines/giro3d/displayCrsFramePolicy';

export const DEFAULT_BASEMAP_LAYER_ID = 'layer:basemap:osm-default';
export const DEFAULT_SATELLITE_LAYER_ID = 'layer:basemap:esri-satellite';

/** True for the injected synthetic basemaps (OSM, satellite, or any layer stamped with the flag). */
export function isSyntheticBasemapLayer(layer: Pick<Layer, 'id' | 'style'>): boolean {
  if (layer.id === DEFAULT_BASEMAP_LAYER_ID) return true;
  if (layer.id === DEFAULT_SATELLITE_LAYER_ID) return true;
  const style = layer.style as { isSyntheticBasemap?: boolean } | undefined;
  return style?.isSyntheticBasemap === true;
}

export function createDefaultOsmBasemapLayer(): Layer {
  // Prefer same-origin Vite proxies in the browser so corporate tunnels
  // cannot blank the map (ERR_TUNNEL_CONNECTION_FAILED on OSM/Esri CDNs).
  const cartoTiles =
    typeof window !== 'undefined'
      ? `${window.location.origin}/basemap-carto/light_all/{z}/{x}/{y}.png`
      : '/basemap-carto/light_all/{z}/{x}/{y}.png';
  return {
    id: DEFAULT_BASEMAP_LAYER_ID,
    tenantId: 'system',
    stacItemId: 'osm-default',
    kind: 'raster',
    crs: 'EPSG:3857',
    bbox: [-180, -85, 180, 85],
    endpoints: {
      tiles: cartoTiles,
    },
    style: {
      title: 'Streets (Carto)',
      family: 'basemap',
      gtwinSlot: 'basemaps',
      gtwinLayerKind: 'raster',
      coordinateMode: 'georeferenced',
      hasExplicitBounds: true,
      hasExplicitCrs: true,
      maxZoom: 18,
      minZoom: 0,
      isSyntheticBasemap: true,
    },
    visibility: { minZoom: 0, maxZoom: 19 },
    permissions: { roles: [], classification: 'tenant' },
    label: 'Streets basemap',
  } as Layer;
}

export function createSatelliteBasemapLayer(): Layer {
  const esriTiles =
    typeof window !== 'undefined'
      ? `${window.location.origin}/basemap-esri/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`
      : '/basemap-esri/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
  return {
    id: DEFAULT_SATELLITE_LAYER_ID,
    tenantId: 'system',
    stacItemId: 'esri-satellite',
    kind: 'raster',
    crs: 'EPSG:3857',
    bbox: [-180, -85, 180, 85],
    endpoints: {
      tiles: esriTiles,
    },
    style: {
      title: 'Esri World Imagery',
      family: 'imagery',
      gtwinSlot: 'basemaps',
      gtwinLayerKind: 'raster',
      coordinateMode: 'georeferenced',
      hasExplicitBounds: true,
      hasExplicitCrs: true,
      maxZoom: 19,
      minZoom: 0,
      isSyntheticBasemap: true,
    },
    visibility: { minZoom: 0, maxZoom: 20 },
    permissions: { roles: [], classification: 'tenant' },
    label: 'Esri World Imagery',
  } as Layer;
}

export interface BasemapInjection {
  layers: Layer[];
  slotEntries: Array<{ layerId: string; slot: SceneSlot }>;
  visibilityEntries: Array<{ layerId: string; visible: boolean }>;
  injected: boolean;
  /** Layer IDs of basemaps that were suppressed due to CRS incompatibility. */
  suppressedBasemapIds: string[];
}

/**
 * If the provided slot entries contain at least one `basemaps` entry, return
 * the inputs unchanged. Otherwise inject synthetic OSM + satellite basemaps
 * so they render first (bottom of stack) and show up as layer cards.
 *
 * All Web Mercator basemaps (both injected and pre-existing) are suppressed when
 * a layer that can actually move the render frame declares a projected display
 * CRS (ADR-023) — point clouds, meshes and 3D Tiles. A raster or terrain layer
 * declaring one does not suppress anything: those render on the EPSG:3857 Giro3D
 * `Map` regardless, so they still need ground beneath them.
 */
export function maybeInjectDefaultBasemap(
  layers: Layer[],
  slotEntries: Array<{ layerId: string; slot: SceneSlot }>,
  visibilityEntries: Array<{ layerId: string; visible: boolean }>,
): BasemapInjection {
  const hasBasemap = slotEntries.some((entry) => entry.slot === 'basemaps');

  // Only a layer that can actually pull the scene into its `displayCrs` makes a
  // Web Mercator basemap wrong. Raster and terrain layers cannot: their tiles are
  // `WebMercatorQuad` and they render on the EPSG:3857 Giro3D `Map`, so a DTM
  // stamped `EPSG:32636` used to suppress the basemap for a scene that renders in
  // Mercator anyway — leaving the terrain floating over the clear colour with
  // nothing behind it.
  const frameCrs = layers.reduce<string | undefined>((found, layer) => {
    if (found) return found;
    if (!kindCanSwitchRenderFrame(layer.kind)) return undefined;
    if (typeof layer.displayCrs === 'string' && layer.displayCrs.toUpperCase() !== 'EPSG:3857') {
      return layer.displayCrs;
    }
    return undefined;
  }, undefined);

  if (hasBasemap) {
    const suppressedBasemapIds: string[] = [];
    if (frameCrs) {
      for (const entry of slotEntries) {
        if (entry.slot !== 'basemaps') continue;
        const layer = layers.find((l) => l.id === entry.layerId);
        if (layer && !isBasemapCrsCompatible(layer.crs ?? 'EPSG:3857', frameCrs)) {
          suppressedBasemapIds.push(entry.layerId);
        }
      }
    }
    return { layers, slotEntries, visibilityEntries, injected: false, suppressedBasemapIds };
  }

  if (frameCrs) {
    return {
      layers, slotEntries, visibilityEntries,
      injected: false,
      suppressedBasemapIds: [DEFAULT_BASEMAP_LAYER_ID, DEFAULT_SATELLITE_LAYER_ID],
    };
  }

  const osm = createDefaultOsmBasemapLayer();
  const satellite = createSatelliteBasemapLayer();
  return {
    layers: [osm, satellite, ...layers],
    slotEntries: [
      { layerId: osm.id, slot: 'basemaps' },
      { layerId: satellite.id, slot: 'basemaps' },
      ...slotEntries,
    ],
    visibilityEntries: [
      { layerId: osm.id, visible: true },
      { layerId: satellite.id, visible: false },
      ...visibilityEntries,
    ],
    injected: true,
    suppressedBasemapIds: [],
  };
}
